import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Users, 
  Filter,
  Search,
  Download,
  Plus,
  Edit,
  Eye,
  Calendar,
  User,
  Tag,
  BarChart3,
  PieChart,
  Activity,
  Bell,
  FileText,
  Settings,
  RefreshCw,
  X,
  Target,
  Trash2
} from 'lucide-react';
import { enhancedApi } from '../services/api';
import { Pagination } from '../components/Pagination';
import { useToast } from '../contexts/ToastContext';

interface DashboardStats {
  total_recommendations: number;
  overdue_count: number;
  status_breakdown: Record<string, number>;
  priority_breakdown: Record<string, number>;
  category_breakdown: Array<{
    name: string;
    color: string;
    count: number;
  }>;
  recent_activity: Array<{
    id: number;
    title: string;
    status: string;
    updated_at: string;
    updated_by_name: string;
    patient_id: string;
  }>;
}

interface Recommendation {
  id: number;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  category_name: string;
  category_color: string;
  assigned_to_name: string;
  assigned_to_email: string;
  lead_person: string;
  due_date: string;
  due_status: 'overdue' | 'due_today' | 'on_track';
  patient_id: string;
  case_type: string;
  created_at: string;
  audit_form_id: number;
  assignment_id: number;
  updated_at: string;
  next_followup_date: string | null;
  days_until_next_followup: number | null;
  action_plan: string;
  followups?: Array<{
    id: number;
    followup_type: string;
    notes: string;
    status: string;
    followup_date: string;
    next_followup_date: string;
    created_by_name: string;
    created_at: string;
  }>;
}

interface FilterState {
  status: string;
  priority: string;
  category_id: string;
  assigned_to: string;
  due_date_from: string;
  due_date_to: string;
  search: string;
}

export const RecommendationFollowup: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [showFollowupsModal, setShowFollowupsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [followups, setFollowups] = useState<any[]>([]);
  const [followupForm, setFollowupForm] = useState({
    followup_type: 'progress_check',
    notes: '',
    status: 'satisfactory',
    followup_date: new Date().toISOString().split('T')[0],
    next_followup_date: ''
  });
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    lead_person: '',
    due_date: '',
    status: 'pending',
    action_plan: ''
  });
  const [filters, setFilters] = useState<FilterState>({
    status: '',
    priority: '',
    category_id: '',
    assigned_to: '',
    due_date_from: '',
    due_date_to: '',
    search: ''
  });

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const response = await enhancedApi.recommendationFollowup.getDashboard();
      if (response.success) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  }, []);

  // Load recommendations
  const loadRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());
      
      const response = await enhancedApi.recommendationFollowup.getRecommendations(params.toString());
      if (response.success) {
        setRecommendations(response.data);
        setTotalPages(response.pagination?.pages || 1);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    loadRecommendations();
  }, [loadRecommendations]);

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category_id: '',
      assigned_to: '',
      due_date_from: '',
      due_date_to: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const handleSelectRecommendation = (id: number) => {
    setSelectedRecommendations(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecommendations.length === recommendations.length) {
      setSelectedRecommendations([]);
    } else {
      setSelectedRecommendations(recommendations.map(r => r.id));
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDueStatusColor = (dueStatus: string) => {
    switch (dueStatus) {
      case 'overdue': return 'text-red-600 font-semibold';
      case 'due_today': return 'text-orange-600 font-semibold';
      case 'on_track': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const handleViewRecommendation = async (id: number) => {
    try {
      const response = await enhancedApi.recommendationFollowup.getRecommendationDetails(id);
      if (response.success) {
        setSelectedRecommendation(response.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Failed to load recommendation details:', error);
    }
  };

  const handleEditRecommendation = async (id: number) => {
    try {
      const response = await enhancedApi.recommendationFollowup.getRecommendationDetails(id);
      if (response.success) {
        setSelectedRecommendation(response.data);
        setEditForm({
          title: response.data.title || '',
          description: response.data.description || '',
          priority: response.data.priority || 'medium',
          lead_person: response.data.lead_person || '',
          due_date: response.data.due_date || '',
          status: response.data.status || 'pending',
          action_plan: response.data.action_plan || ''
        });
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('Failed to load recommendation details:', error);
    }
  };

  const handleViewAudit = (auditFormId: number) => {
    // Navigate to the review page
    navigate(`/review/${auditFormId}`);
  };

  const handleAddFollowup = (id: number) => {
    const recommendation = recommendations.find(r => r.id === id);
    if (recommendation) {
      setSelectedRecommendation(recommendation);
      setShowFollowupModal(true);
    }
  };

  const handleViewFollowups = async (id: number) => {
    try {
      const response = await enhancedApi.recommendationFollowup.getFollowups(id);
      if (response.success) {
        setFollowups(response.data);
        setSelectedRecommendation(recommendations.find(r => r.id === id) || null);
        setShowFollowupsModal(true);
      }
    } catch (error) {
      console.error('Failed to load follow-ups:', error);
    }
  };

  const handleCreateFollowup = async () => {
    if (!selectedRecommendation || !followupForm.notes.trim()) {
      return;
    }

    try {
      const response = await enhancedApi.recommendationFollowup.createFollowup({
        recommendation_id: selectedRecommendation.id,
        ...followupForm
      });

      if (response.success) {
        setShowFollowupModal(false);
        setFollowupForm({
          followup_type: 'progress_check',
          notes: '',
          status: 'satisfactory',
          followup_date: new Date().toISOString().split('T')[0],
          next_followup_date: ''
        });
        // Refresh recommendations
        loadRecommendations();
      }
    } catch (error) {
      console.error('Failed to create follow-up:', error);
    }
  };

  const handleUpdateRecommendation = async () => {
    if (!selectedRecommendation || !editForm.title.trim()) {
      return;
    }

    try {
      const response = await enhancedApi.recommendationFollowup.updateRecommendation({
        id: selectedRecommendation.id,
        ...editForm
      });

      if (response.success) {
        setShowEditModal(false);
        setEditForm({
          title: '',
          description: '',
          priority: 'medium',
          lead_person: '',
          due_date: '',
          status: 'pending',
          action_plan: ''
        });
        // Refresh recommendations
        loadRecommendations();
      }
    } catch (error) {
      console.error('Failed to update recommendation:', error);
    }
  };

  const handleDeleteRecommendation = (id: number) => {
    const recommendation = recommendations.find(r => r.id === id);
    if (!recommendation) return;
    
    setSelectedRecommendation(recommendation);
    setShowDeleteModal(true);
  };

  const confirmDeleteRecommendation = async () => {
    if (!selectedRecommendation) return;

    try {
      const response = await enhancedApi.recommendationFollowup.deleteRecommendation(selectedRecommendation.id);
      if (response.success) {
        addToast({
          type: 'success',
          title: 'Success',
          message: 'Recommendation deleted successfully'
        });
        setRecommendations(prev => prev.filter(r => r.id !== selectedRecommendation.id));
        setSelectedRecommendations(prev => prev.filter(selectedId => selectedId !== selectedRecommendation.id));
        setShowDeleteModal(false);
        setSelectedRecommendation(null);
      } else {
        addToast({
          type: 'error',
          title: 'Error',
          message: response.message || 'Failed to delete recommendation'
        });
      }
    } catch (error) {
      console.error('Failed to delete recommendation:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete recommendation'
      });
    }
  };

  const handleBulkDeleteRecommendations = () => {
    if (selectedRecommendations.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDeleteRecommendations = async () => {
    if (selectedRecommendations.length === 0) return;

    try {
      const response = await enhancedApi.recommendationFollowup.bulkDeleteRecommendations(selectedRecommendations);
      if (response.success) {
        addToast({
          type: 'success',
          title: 'Success',
          message: `Successfully deleted ${selectedRecommendations.length} recommendations`
        });
        setRecommendations(prev => prev.filter(r => !selectedRecommendations.includes(r.id)));
        setSelectedRecommendations([]);
        setShowBulkActions(false);
        setShowBulkDeleteModal(false);
      } else {
        addToast({
          type: 'error',
          title: 'Error',
          message: response.message || 'Failed to delete recommendations'
        });
      }
    } catch (error) {
      console.error('Failed to bulk delete recommendations:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete recommendations'
      });
    }
  };

  if (loading && !dashboardStats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Recommendation Follow-up</h1>
              <p className="text-blue-100">Track and manage recommendations from approved audit forms</p>
            </div>
          </div>
        </div>
      </div>

      
        {/* Dashboard Stats */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Recommendations</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_recommendations}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{dashboardStats.overdue_count}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{dashboardStats.status_breakdown.completed || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">In Progress</p>
                  <p className="text-2xl font-bold text-yellow-600">{dashboardStats.status_breakdown.in_progress || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Row */}
        {dashboardStats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Status Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
              <div className="space-y-3">
                {Object.entries(dashboardStats.status_breakdown).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 capitalize">{status.replace('_', ' ')}</span>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${(count / dashboardStats.total_recommendations) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Breakdown</h3>
              <div className="space-y-3">
                {dashboardStats.category_breakdown.map((category) => (
                  <div key={category.name} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-3" 
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span className="text-sm font-medium text-gray-600">{category.name}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{category.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recommendations</h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="btn btn-outline"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </button>
                {selectedRecommendations.length > 0 && (
                  <button
                    onClick={() => setShowBulkActions(!showBulkActions)}
                    className="btn btn-primary"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Bulk Actions ({selectedRecommendations.length})
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Priority</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date From</label>
                  <input
                    type="date"
                    value={filters.due_date_from}
                    onChange={(e) => handleFilterChange('due_date_from', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date To</label>
                  <input
                    type="date"
                    value={filters.due_date_to}
                    onChange={(e) => handleFilterChange('due_date_to', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search recommendations..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={clearFilters}
                  className="btn btn-outline"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}

          {/* Bulk Actions Panel */}
          {showBulkActions && (
            <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">
                  {selectedRecommendations.length} recommendations selected
                </span>
                <button className="btn btn-sm btn-primary">
                  Update Status
                </button>
                <button className="btn btn-sm btn-outline">
                  Assign User
                </button>
                <button className="btn btn-sm btn-outline">
                  Set Due Date
                </button>
                <button 
                  onClick={handleBulkDeleteRecommendations}
                  className="btn btn-sm bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recommendations Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRecommendations.length === recommendations.length && recommendations.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recommendation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Next Follow-up
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Audit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    </td>
                  </tr>
                ) : recommendations.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      No recommendations found
                    </td>
                  </tr>
                ) : (
                  recommendations.map((recommendation) => (
                    <tr key={recommendation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedRecommendations.includes(recommendation.id)}
                          onChange={() => handleSelectRecommendation(recommendation.id)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: recommendation.category_color }}
                            ></div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {recommendation.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {recommendation.category_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(recommendation.priority)}`}>
                          {recommendation.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(recommendation.status)}`}>
                          {recommendation.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {recommendation.lead_person || recommendation.assigned_to_name || 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(recommendation.due_date).toLocaleDateString()}
                        </div>
                        <div className={`text-xs ${getDueStatusColor(recommendation.due_status)}`}>
                          {recommendation.due_status.replace('_', ' ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {recommendation.next_followup_date ? (
                          <div>
                            <div className="text-sm text-gray-900">
                              {new Date(recommendation.next_followup_date).toLocaleDateString()}
                            </div>
                            <div className={`text-xs ${
                              recommendation.days_until_next_followup === 0 ? 'text-orange-600 font-semibold' :
                              recommendation.days_until_next_followup && recommendation.days_until_next_followup < 0 ? 'text-red-600 font-semibold' :
                              'text-green-600'
                            }`}>
                              {recommendation.days_until_next_followup === 0 ? 'Due today' :
                               recommendation.days_until_next_followup && recommendation.days_until_next_followup < 0 ? `${Math.abs(recommendation.days_until_next_followup)} days overdue` :
                               recommendation.days_until_next_followup ? `${recommendation.days_until_next_followup} days left` :
                               'No follow-up scheduled'}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">No follow-up scheduled</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={() => handleViewAudit(recommendation.audit_form_id)}
                          className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 px-2 py-1 rounded-md transition-colors"
                          title="View Audit Form"
                        >
                          <FileText className="w-4 h-4" />
                          <span className="text-sm font-medium">View Audit</span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => handleViewRecommendation(recommendation.id)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleEditRecommendation(recommendation.id)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleViewFollowups(recommendation.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Follow-ups"
                          >
                            <Activity className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleAddFollowup(recommendation.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Add Follow-up"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteRecommendation(recommendation.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {recommendations.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              itemsPerPageOptions={[10, 20, 50, 100]}
            />
          )}
        </div>

        {/* Recommendation Detail Modal */}
        {showDetailModal && selectedRecommendation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Recommendation Details</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedRecommendation.title}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedRecommendation.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Priority</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(selectedRecommendation.priority)}`}>
                        {selectedRecommendation.priority}
                      </span>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(selectedRecommendation.status)}`}>
                        {selectedRecommendation.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Lead Person</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedRecommendation.lead_person || selectedRecommendation.assigned_to_name || 'Unassigned'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Due Date</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {new Date(selectedRecommendation.due_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Audit</label>
                    <button
                      onClick={() => handleViewAudit(selectedRecommendation.audit_form_id)}
                      className="mt-1 flex items-center space-x-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 px-3 py-2 rounded-md transition-colors"
                      title="View Audit Form"
                    >
                      <FileText className="w-4 h-4" />
                      <span className="text-sm font-medium">View Audit Form</span>
                    </button>
                  </div>
                  
                  {selectedRecommendation.action_plan && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Action Plan</label>
                      <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRecommendation.action_plan}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedRecommendation.followups && selectedRecommendation.followups.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Follow-ups</label>
                      <div className="space-y-2">
                        {selectedRecommendation.followups.map((followup, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{followup.followup_type.replace('_', ' ')}</p>
                                <p className="text-sm text-gray-600">{followup.notes}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">{followup.created_by_name}</p>
                                <p className="text-xs text-gray-500">{new Date(followup.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="btn btn-outline"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEditRecommendation(selectedRecommendation.id);
                    }}
                    className="btn btn-primary"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Follow-up Modal */}
        {showFollowupModal && selectedRecommendation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Add Follow-up</h3>
                  <button
                    onClick={() => setShowFollowupModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Follow-up Type</label>
                    <select 
                      value={followupForm.followup_type}
                      onChange={(e) => setFollowupForm(prev => ({ ...prev, followup_type: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="progress_check">Progress Check</option>
                      <option value="completion_verification">Completion Verification</option>
                      <option value="escalation">Escalation</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      rows={4}
                      value={followupForm.notes}
                      onChange={(e) => setFollowupForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter follow-up notes..."
                    ></textarea>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select 
                      value={followupForm.status}
                      onChange={(e) => setFollowupForm(prev => ({ ...prev, status: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="satisfactory">Satisfactory</option>
                      <option value="needs_improvement">Needs Improvement</option>
                      <option value="escalated">Escalated</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Follow-up Date</label>
                      <input
                        type="date"
                        value={followupForm.followup_date}
                        onChange={(e) => setFollowupForm(prev => ({ ...prev, followup_date: e.target.value }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Next Follow-up Date</label>
                      <input
                        type="date"
                        value={followupForm.next_followup_date}
                        onChange={(e) => setFollowupForm(prev => ({ ...prev, next_followup_date: e.target.value }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </form>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowFollowupModal(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateFollowup}
                    className="btn btn-primary"
                  >
                    Add Follow-up
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Follow-ups Modal */}
        {showFollowupsModal && selectedRecommendation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Follow-ups for: {selectedRecommendation.title}
                  </h3>
                  <button
                    onClick={() => setShowFollowupsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {followups.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No follow-ups found for this recommendation.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {followups.map((followup, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              followup.followup_type === 'escalation' ? 'bg-red-100 text-red-800' :
                              followup.followup_type === 'completion_verification' ? 'bg-green-100 text-green-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {followup.followup_type.replace('_', ' ')}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              followup.status === 'escalated' ? 'bg-red-100 text-red-800' :
                              followup.status === 'needs_improvement' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {followup.status.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(followup.followup_date).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-2">{followup.notes}</p>
                        
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>Created by: {followup.created_by_name}</span>
                          <span>{new Date(followup.created_at).toLocaleDateString()}</span>
                        </div>
                        
                        {followup.next_followup_date && (
                          <div className="mt-2 text-xs text-blue-600">
                            Next follow-up: {new Date(followup.next_followup_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowFollowupsModal(false)}
                    className="btn btn-outline"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowFollowupsModal(false);
                      handleAddFollowup(selectedRecommendation.id);
                    }}
                    className="btn btn-primary"
                  >
                    Add New Follow-up
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Recommendation Modal */}
        {showEditModal && selectedRecommendation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Edit Recommendation
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter recommendation title"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      rows={4}
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter recommendation description"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Priority</label>
                      <select
                        value={editForm.priority}
                        onChange={(e) => setEditForm(prev => ({ ...prev, priority: e.target.value }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Lead Person</label>
                    <input
                      type="text"
                      value={editForm.lead_person}
                      onChange={(e) => setEditForm(prev => ({ ...prev, lead_person: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter lead person"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Action Plan</label>
                    <textarea
                      rows={4}
                      value={editForm.action_plan}
                      onChange={(e) => setEditForm(prev => ({ ...prev, action_plan: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter action plan"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Due Date</label>
                    <input
                      type="date"
                      value={editForm.due_date}
                      onChange={(e) => setEditForm(prev => ({ ...prev, due_date: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </form>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateRecommendation}
                    className="btn btn-primary"
                  >
                    Update Recommendation
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Recommendation Modal */}
        {showDeleteModal && selectedRecommendation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Recommendation</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to permanently delete this recommendation?
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm">
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Title:</span>
                      <p className="text-gray-900">{selectedRecommendation.title}</p>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Category:</span>
                      <p className="text-gray-900">{selectedRecommendation.category_name}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Priority:</span>
                      <p className="text-gray-900 capitalize">{selectedRecommendation.priority}</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-red-600 mt-3">
                  ⚠️ This will also delete all follow-ups associated with this recommendation.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedRecommendation(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDeleteRecommendation}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-2 inline" />
                  Delete Recommendation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Recommendations Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Multiple Recommendations</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to permanently delete <strong>{selectedRecommendations.length}</strong> selected recommendations?
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">
                    <p className="mb-2">This will delete:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>{selectedRecommendations.length} recommendations</li>
                      <li>All associated follow-ups</li>
                      <li>All related data and history</li>
                    </ul>
                  </div>
                </div>
                <p className="text-sm text-red-600 mt-3">
                  ⚠️ This action cannot be undone.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button 
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmBulkDeleteRecommendations}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-2 inline" />
                  Delete {selectedRecommendations.length} Recommendations
                </button>
              </div>
            </div>
          </div>
        )}
      
    </div>
  );
};
